import { Fragment } from 'react';
import cn from 'clsx';

import { Dialog, Transition } from '@headlessui/react';
import ButtonMighty from '../ButtonMighty';

interface ConfirmDialogProps {
  open?: boolean;
  loading?: boolean;
  onCancel?: () => any;
  onConfirm?: () => any;
  title?: string;
  content?: string | JSX.Element;
  className?: string;
  cancelText?: string;
  confirmText?: string;
  cancelButton?: JSX.Element;
  confirmButton?: JSX.Element;
}

export function ConfirmDialog({
  open = false,
  loading = false,
  onCancel,
  onConfirm,
  title,
  content,
  className,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  cancelButton,
  confirmButton,
}: ConfirmDialogProps): React.ReactElement {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        open={open}
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={() => onCancel?.()}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-70" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className={cn(
                'inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg',
                className
              )}
            >
              {title && (
                <Dialog.Title as="h3" className="text-lg font-bold leading-6">
                  {title}
                </Dialog.Title>
              )}

              {content && (
                <div className="mt-2">
                  {typeof content === 'string' ? (
                    <p className="text-base whitespace-pre-line">{content}</p>
                  ) : (
                    <div children={content} />
                  )}
                </div>
              )}

              {(!content || typeof content === 'string') && (
                <div className="flex flex-col mt-8 space-y-4">
                  <div>
                    <ButtonMighty
                      disabled={loading}
                      color="#f00221"
                      text={cancelText}
                      onClick={() => onCancel?.()}
                      borderColor="#221f20"
                      margin="0"
                      textColor="#221f20"
                      width="auto"
                    />
                  </div>

                  <div>
                    <ButtonMighty
                      disabled={loading}
                      loading={loading}
                      text={confirmText}
                      onClick={onConfirm}
                      color="#00ff43"
                      borderColor="#221f20"
                      textColor="#221f20"
                      margin="0"
                      width="auto"
                    />
                  </div>
                </div>
              )}
              <button className="absolute bottom-0 opacity-0"></button>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ConfirmDialog;
